import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  createElement,
  type ReactNode,
} from 'react';
import type {
  Tier1Inputs,
  BriefSection,
  CanvasSession,
  CanvasSessionStatus,
  ClusterId,
  SectionData,
} from '../types/canvas';
import { useGenerateStream, type GenerateStreamStatus } from './use-generate-stream';

export type { Tier1Inputs, BriefSection, CanvasSession, CanvasSessionStatus, ClusterId, SectionData };

export interface BriefState {
  session: CanvasSession | null;
  loading: boolean;
  error: string | null;
  streamStatus: GenerateStreamStatus;
  load: (id: string) => Promise<void>;
  generate: (inputs: Tier1Inputs) => Promise<string>;
  startStream: (inputs: Tier1Inputs) => void;
  abortStream: () => void;
  patchSection: (updated: BriefSection) => void;
  patchApproval: (sectionId: string, approvalCount: number) => void;
}

const BriefStateContext = createContext<BriefState | undefined>(undefined);

export function BriefStateProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<CanvasSession | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stream = useGenerateStream();

  useEffect(() => {
    if (stream.state.status === 'streaming' || stream.state.status === 'complete') {
      setSession((prev) => {
        if (!prev) {
          return {
            sessionId: stream.state.sessionId ?? '',
            pseudonymSlug: '',
            tier1Inputs: { genre: '', emotionalCore: '', compTitles: '', scene: '', toneDarkness: 3 as const },
            sections: stream.state.sections,
            approvalCount: 0,
            status: stream.state.status === 'complete' ? 'active' : 'generating',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
        }
        return {
          ...prev,
          sessionId: stream.state.sessionId ?? prev.sessionId,
          sections: stream.state.sections,
          status: stream.state.status === 'complete' ? 'active' : prev.status,
          updatedAt: new Date().toISOString(),
        };
      });
    }
  }, [stream.state.status, stream.state.sections, stream.state.sessionId]);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/brief/session/${id}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load brief');
      const data: CanvasSession = await res.json();
      setSession(data);
      if (data.status === 'generating') {
        stream.connect(id);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [stream]);

  const generate = useCallback(async (inputs: Tier1Inputs): Promise<string> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/brief/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(inputs),
      });
      if (!res.ok) throw new Error('Failed to generate brief');
      const data: CanvasSession = await res.json();
      setSession(data);
      return data.sessionId;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const startStream = useCallback((inputs: Tier1Inputs) => {
    setSession(null);
    setError(null);
    stream.start(inputs);
  }, [stream]);

  const abortStream = useCallback(() => {
    stream.abort();
  }, [stream]);

  const patchSection = useCallback((updated: BriefSection) => {
    setSession((prev) => {
      if (!prev) return prev;
      const sections = prev.sections.map((s) => s.id === updated.id ? updated : s);
      return { ...prev, sections, updatedAt: new Date().toISOString() };
    });
  }, []);

  const patchApproval = useCallback((sectionId: string, approvalCount: number) => {
    setSession((prev) => {
      if (!prev) return prev;
      const sections = prev.sections.map((s) =>
        s.id === sectionId ? { ...s, status: 'approved' as const, approvedAt: new Date().toISOString() } : s
      );
      return { ...prev, sections, approvalCount, updatedAt: new Date().toISOString() };
    });
  }, []);

  return createElement(
    BriefStateContext.Provider,
    { value: { session, loading, error, streamStatus: stream.state.status, load, generate, startStream, abortStream, patchSection, patchApproval } },
    children,
  );
}

export function useBriefState(): BriefState {
  const ctx = useContext(BriefStateContext);
  if (ctx === undefined) {
    throw new Error('useBriefState must be used within a BriefStateProvider');
  }
  return ctx;
}
